import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import { UpdateBlogDataInput_V2 } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { IAnalyticsLogger } from '@/imports/ui/amplitude/types';

import {
  useUpdateBlog_updateBlogV2,
  useUpdateBlog_updateBlogV2Variables,
} from './graphql-types/useUpdateBlog_updateBlogV2';

const updateBlogWithPostAST = gql`
  mutation useUpdateBlog_updateBlogV2($input: UpdateBlogInput_V2!) {
    updateBlogV2(input: $input) {
      data {
        _id
        scheduledPublishDate
        isAnnouncementScheduled
        organization {
          _id
          name
        }
        group {
          _id
          name
          isGlobalSingleton
        }
        published
        title
        content
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        tags {
          _id
          name
        }
      }
    }
  }
`;

type BlogMutationOptions = {
  analyticsClient: IAnalyticsLogger;
  publish: boolean;
  isAnnouncement: boolean;
  alreadyPublished: boolean;
};

type UpdateBlogOptions = {
  postId: string;
  options: BlogMutationOptions;
  blog: UpdateBlogDataInput_V2 & { organizationId?: string };
};

export const useUpdateBlog = (): {
  updateBlog: (options: UpdateBlogOptions) => Promise<boolean>;
} => {
  const [updateFunc] = useMutation<
    useUpdateBlog_updateBlogV2,
    useUpdateBlog_updateBlogV2Variables
  >(updateBlogWithPostAST, {
    refetchQueries: ['DraftsPage_GetMyDraftPosts', 'AccountMenu_Query'],
  });

  const updateBlog = React.useCallback(
    async (options: UpdateBlogOptions) => {
      const { analyticsClient, publish, isAnnouncement, alreadyPublished } =
        options.options;

      const blog = options.blog;

      try {
        const result = await updateFunc({
          variables: {
            input: {
              data: {
                ...blog,
                published: publish,
              },
              blogPostId: options.postId,
              isAnnouncement: isAnnouncement,
            },
          },
        });

        if (!alreadyPublished && publish) {
          const postedIn = !!result.data?.updateBlogV2.data?.group
            ?.isGlobalSingleton
            ? 'general'
            : !!blog?.organizationId
            ? 'organization'
            : 'group';
          analyticsClient.logEvent_comm_click_publish({
            'banner image': !!blog.blogBannerImageId,
            'added tags': !!blog.tagIds && blog.tagIds.length > 0,
            announcement: !!isAnnouncement,
            'posted in': postedIn,
            'is hosted by sponsor': false,
          });
        }

        return true;
      } catch (err) {
        logError(err);
        return false;
      }
    },
    [updateFunc]
  );

  return {
    updateBlog,
  };
};
